"use client";

import React from "react";
import { Clock, MapPin, PhoneCall, ShieldCheck, Truck, Wrench, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const keyHighlights = [{
  icon: Clock,
  title: "25–35 Min ETA",
  description: "Rapid dispatch along GA-138 and Hudson Bridge Rd corridor."
}, {
  icon: MapPin,
  title: "Henry County Border",
  description: "Full coverage across Stockbridge, Eagles Landing, & surrounding roads."
}, {
  icon: Truck,
  title: "Fully Stocked Vans",
  description: "On-site fixes for leaks, burst pipes, and water heaters on the first trip."
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  description: "Verified master technicians on-call 24 hours a day, 7 days a week."
}];
const coverageAreas = ["Downtown Stockbridge & City Center", "Eagles Landing & Country Club Enclave", "Hudson Bridge Road Commercial & Residential", "Flippen / Speer Road Vicinity", "Highway 138 Corridor to I-75", "Rock Quarry Road Residential Neighborhoods"];
const urgentServices = [{
  name: "Emergency Pipe Repair",
  path: "/pipe-repair/"
}, {
  name: "Water Heater Repair",
  path: "/water-heater-repair/"
}, {
  name: "Sewer Line Diagnostics",
  path: "/sewer-line-repair/"
}, {
  name: "Drain Clearing Service",
  path: "/drain-cleaning/"
}];
export default function StockbridgeServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="stockbridgeservicearea" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Henry County Service Hub
          </Badge>
          
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Stockbridge Emergency Plumbing Response
          </h2>
          
          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Positioned right along the Henry County border, our mobile emergency plumbing teams reach Stockbridge residential and commercial properties within <strong className="text-foreground font-semibold">25 to 35 minutes</strong>.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {keyHighlights.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={index} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex flex-col items-start text-left">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground mb-4">
                      <IconComponent className="size-6" />
                    </div>
                    <h3 className="font-default text-lg font-bold text-card-foreground mb-2">
                      {item.title}
                    </h3>
                    <p className="font-sans text-sm text-muted-foreground leading-normal">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Main Content Layout (2 Columns) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Left Column: Community Expertise & Logistics */}
          <div className="lg:col-span-7 flex flex-col justify-between rounded-2xl bg-muted/40 border border-border p-6 sm:p-8">
            <div>
              <div className="flex items-center gap-2 text-primary mb-3">
                <Wrench className="size-5" />
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                  Direct Dispatch Reliability
                </span>
              </div>

              <h3 className="font-default text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Rapid Local Plumbing Resolution for Stockbridge
              </h3>

              <p className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                From high water pressure spikes near Eagles Landing to sudden main sewer backups off Hudson Bridge Road, plumbing emergencies require an immediate, knowledgeable presence. Our local technicians carry complete digital diagnostic scopes, heavy-duty snakes, and pipe replacement fittings to resolve failures on the initial visit.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {coverageAreas.map((area, idx) => <div key={area} data-index={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="font-sans text-xs sm:text-sm text-foreground">{area}</span>
                  </div>)}
              </div>
            </div>

            <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto font-medium gap-2">
                  <PhoneCall className="size-4" />
                  Call Dispatch: (470) 847-9076
                </Button>
              </a>
              <Link to="/service-areas/" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium gap-2">
                  All Service Areas
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Column: Fast Dispatch Services */}
          <div className="lg:col-span-5 flex flex-col justify-between rounded-2xl bg-card border border-border p-6 sm:p-8 shadow-sm">
            <div>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold mb-4">
                Available 24 Hours / 7 Days
              </div>

              <h3 className="font-default text-xl sm:text-2xl font-bold text-card-foreground mb-2">
                Emergency Services in Stockbridge
              </h3>
              
              <p className="font-sans text-xs sm:text-sm text-muted-foreground mb-6">
                Instant priority booking with zero after-hours dispatch surcharges. Select an emergency below:
              </p>

              <div className="space-y-3 mb-6">
                {urgentServices.map((service, index) => <Link key={service.name} to={service.path} className="block group">
                    <div data-index={index} className="flex items-center justify-between p-3.5 rounded-lg border border-border bg-background hover:bg-secondary/50 transition-colors">
                      <span className="font-sans text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        {service.name}
                      </span>
                      <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </Link>)}
              </div>
            </div>

            <div className="rounded-xl bg-secondary/50 p-4 border border-border/80">
              <p className="font-sans text-xs text-secondary-foreground leading-relaxed">
                <strong>Need an accurate quote?</strong> Our technicians provide straightforward upfront evaluations before starting any repair or replacement work in Stockbridge.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}